import axios from "axios";

const ADMIN_API_BASE_URL = "/ofc/api/v1/admin/configurations";
const PUBLIC_API_BASE_URL = "/ofc/api/v1/configurations";

// Configure axios defaults
axios.defaults.withCredentials = true;

export interface SystemConfiguration {
  id?: number;
  configKey: string;
  configValue: string;
  description?: string;
  isSensitive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PublicConfiguration {
  configKey: string;
  configValue: string;
  description?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class ConfigurationApiService {
  // ========== ADMIN ENDPOINTS ==========

  /**
   * Get all system configurations (Admin only)
   */
  async getAllConfigurations(): Promise<SystemConfiguration[]> {
    const response =
      await axios.get<ApiResponse<SystemConfiguration[]>>(ADMIN_API_BASE_URL);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch configurations");
    }
    return response.data.data;
  }

  /**
   * Get a specific configuration by key (Admin only)
   */
  async getConfigurationByKey(configKey: string): Promise<SystemConfiguration> {
    const response = await axios.get<ApiResponse<SystemConfiguration>>(
      `${ADMIN_API_BASE_URL}/${configKey}`,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch configuration");
    }
    return response.data.data;
  }

  /**
   * Update a single configuration (Admin only)
   */
  async updateConfiguration(
    configKey: string,
    configValue: string,
  ): Promise<SystemConfiguration> {
    const response = await axios.put<ApiResponse<SystemConfiguration>>(
      `${ADMIN_API_BASE_URL}/${configKey}`,
      {
        configKey,
        configValue,
      },
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update configuration");
    }
    return response.data.data;
  }

  /**
   * Update multiple configurations at once (Admin only)
   */
  async updateConfigurations(
    configurations: SystemConfiguration[],
  ): Promise<SystemConfiguration[]> {
    const response = await axios.put<ApiResponse<SystemConfiguration[]>>(
      ADMIN_API_BASE_URL,
      configurations,
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to update configurations");
    }
    return response.data.data;
  }

  // ========== PUBLIC ENDPOINTS ==========

  /**
   * Get public configurations (Authenticated users only)
   * Currently returns: Telework Link
   */
  async getPublicConfigurations(): Promise<PublicConfiguration[]> {
    const response =
      await axios.get<ApiResponse<PublicConfiguration[]>>(PUBLIC_API_BASE_URL);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || "Failed to fetch configurations");
    }
    return response.data.data;
  }

  /**
   * Get telework link configuration
   */
  async getTeleworkLink(): Promise<string | null> {
    try {
      const configs = await this.getPublicConfigurations();
      const teleworkConfig = configs.find(
        (c) => c.configKey === "TELEWORK_LINK",
      );
      return teleworkConfig?.configValue || null;
    } catch {
      return null;
    }
  }
}

export const configurationApiService = new ConfigurationApiService();
export default configurationApiService;
